import { DownloadRounded } from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { LoadingButton } from '@mui/lab'
import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'
import FlowApiClient from '../../../backend/sub_api_clients/FlowApiClient'
import { INode, NodeType } from '../../../definitions/Nodes'
import { IExecutionEnvironmentArtifact, INodeExecEnvItem } from '../../../definitions/NodesExecution'
import JSONEditor from '../../editor/JSONEditor'
import useCommand from '../../hooks/useCommand'
import RenderTableData from '../nodes/RenderTableData'

interface P {
    data: INodeExecEnvItem
    showMode: string
    nodeId: string
    eeId: string
    inOut: 'input' | 'output'
    nodeType?: INode<NodeType>['type']
    artifacts?: IExecutionEnvironmentArtifact[]
}

const AuditAccordion = ({ data, showMode, nodeId, eeId, inOut, nodeType, artifacts }: P) => {
    const [value, setValue] = useState(data.value?.memoryData?.data)
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(data.value?.memoryData?.page)

    const { status: downloadArtifactStatus, execute: downloadArtifact } = useCommand({
        commandFn: () => {
            const artifactUUID = artifacts.find(a => a.nodeId === nodeId).uuid
            return FlowApiClient.downloadArtifact(eeId, artifactUUID)
        },
        onFinish: result => {
            window.open(result.response)
        }
    })

    const handleShowMore = async () => {
        if (Array.isArray(value)) {
            setLoading(true)
            const response = await FlowApiClient.getNodeAuditData(nodeId, eeId, data.id, page + 1)
            const newValue = inOut === 'input'
                ? response.inputs.find(i => i.id === data.id)?.value?.memoryData?.data
                : response.outputs.find(o => o.id === data.id)?.value?.memoryData?.data
            setValue(prev => [...prev, ...newValue])
            setPage(prev => prev + 1)
            setLoading(false)
        }
    }

    return (
        <Accordion>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel-${inOut}-${data.key}-content`}
                id={`panel-${inOut}-${data.key}-header`}>
                <Stack flexDirection='row' gap={1} alignItems='center'>
                    {nodeType === 'out_report' || nodeType === 'out_csv'
                        ? (
                                <Box>
                                    <Tooltip title='Download the artifact' arrow>
                                        <IconButton
                                            disabled={downloadArtifactStatus === 'loading'}
                                            size='small'
                                            onClick={e => {
                                                e.stopPropagation()
                                                downloadArtifact(null)
                                            }}>
                                            <DownloadRounded fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )
                        : null}
                    <Typography variant='subtitle1'>{`${data.nodeName} ${data.key}`}</Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Box>
                    {showMode === 'json'
                        ? <JSONEditor value={value ?? ''} readOnly />
                        : <RenderTableData data={value} />}
                </Box>
            </AccordionDetails>
            <Box sx={{ display: 'flex', justifySelf: 'center', marginBottom: 2 }}>
                {data.value.memoryData.totalPages > page && (
                    <LoadingButton
                        size='small'
                        loading={loading}
                        onClick={handleShowMore}>
                        show more
                    </LoadingButton>
                )}
            </Box>
        </Accordion>
    )
}

export default AuditAccordion
